














import {
  CourseModuleStatus,
  CoursePackageStatus
} from '@/jbi-shared/types/jtms.types';
import BaseSwitch from '@/jbi-shared/vue-components/BaseSwitch.vue';
import { CourseModuleComponentItem } from '@/store/modules/jtms/course-module/course-module.types';
import { CoursePackageComponent } from '@/store/modules/jtms/course-package/course-package.types';
import { RootState } from '@/store/store';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, State } from 'vuex-class';
import DisableCourseModulesModal from '../../EditCourses/components/comfirmation-modals/DisableCourseModulesModal.vue';
import DisableCoursePackagesModal from '../../EditCourses/components/comfirmation-modals/DisableCoursePackagesModal.vue';

@Component({
  components: {
    BaseSwitch
  }
})
export default class CourseSettingsToggles extends Vue {
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  enableModules: boolean = false;
  freeCourse: boolean = false;
  enableQuestionnaire: boolean = false;
  isInitializing: boolean = true;

  @Action('course/updateCourseHasModules')
  updateHasModules!: (payload: boolean) => void;

  @Action('course/updateHasQuestionnaire')
  updateHasQuestionnaire!: (payload: boolean) => void;

  @Action('course/updateIsFreeCourse')
  updateIsFreeCourse!: (payload: boolean) => void;

  @Getter('coursePackage/hasPricedCoursePackages')
  hasPricedCoursePackages!: boolean;

  @Getter('coursePackage/pricedCoursePackageCount')
  pricedCoursePackageCount!: boolean;

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasModules!: boolean;

  @State(
    (state: RootState) => state.course.courseCreationState.hasQuestionnaire
  )
  hasQuestionnaires!: boolean;

  @State((state: RootState) => state.course.courseCreationState.isFreeCourse)
  isFreeCourse!: boolean;

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.activeCourseModules
  )
  activeCourseModules!: CourseModuleComponentItem[];

  @State(
    (state: RootState) =>
      state.coursePackage.coursePackageTabState.activeCoursePackages
  )
  activeCoursePackages!: CoursePackageComponent[];

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.courseModuleFormHasError
  )
  courseModuleFormHasError!: boolean;

  get hasDefaultCourseModule() {
    if (!this.activeCourseModules) {
      return false;
    }

    return this.activeCourseModules.some(
      (cm) => cm.status === CourseModuleStatus.DEFAULT
    );
  }

  get hasDefaultCoursePackage() {
    if (!this.activeCoursePackages || this.activeCoursePackages.length === 0) {
      return false;
    }

    return this.activeCoursePackages.some(
      (pkg) => pkg.status === CoursePackageStatus.DEFAULT
    );
  }

  get courseModulesHaveValues() {
    if (!this.activeCourseModules) {
      return false;
    }

    const hasValues: boolean = this.activeCourseModules.some(
      (courseModule) =>
        courseModule.module ||
        (courseModule.trainers ? courseModule.trainers.length > 0 : false) ||
        courseModule.startDate ||
        courseModule.endDate
    );

    return hasValues;
  }

  get coursePackagesHaveValues() {
    if (!this.activeCoursePackages) {
      return false;
    }

    const hasValues: boolean = this.activeCoursePackages.some(
      (pkg) =>
        pkg.currency ||
        (pkg.courseModules ? pkg.courseModules.length > 0 : false) ||
        pkg.amount
    );

    return hasValues;
  }

  mounted() {
    this.$nextTick(() => {
      this.isInitializing = false;
    });
  }

  openDisableCourseModulesModal(): void {
    const confirmationModal = this.$buefy.modal.open({
      parent: this,
      component: DisableCourseModulesModal,
      hasModalCard: true,
      trapFocus: true,
      canCancel: false,
      props: {
        activeCourseModuleCount: this.activeCourseModules.length
      },
      events: {
        confirmDelete: () => {
          this.updateHasModules(this.enableModules);
          confirmationModal.close();
        },
        close: () => {
          // Revert local selection
          this.enableModules = this.hasModules;
          return;
        }
      }
    });
  }

  openDisableCoursePackageModal(): void {
    const confirmationModal = this.$buefy.modal.open({
      parent: this,
      component: DisableCoursePackagesModal,
      hasModalCard: true,
      trapFocus: true,
      canCancel: false,
      props: {
        activeCoursePackageCount: this.pricedCoursePackageCount
      },
      events: {
        confirmDelete: () => {
          this.updateIsFreeCourse(this.freeCourse);
          confirmationModal.close();
        },
        close: () => {
          // Revert local selection
          this.freeCourse = this.isFreeCourse;
          return;
        }
      }
    });
  }

  @Watch('enableModules')
  onModuleToggle() {
    if (
      !this.isInitializing &&
      this.activeCourseModules.length > 0 &&
      !this.hasDefaultCourseModule &&
      this.courseModulesHaveValues &&
      this.enableModules !== this.hasModules
    ) {
      this.openDisableCourseModulesModal();
    } else {
      this.updateHasModules(this.enableModules);
    }
  }

  @Watch('enableQuestionnaire')
  onQuestionnaireToggle() {
    if (false) {
      // JAAS-2803-TODO: Add other appropriate conditions for when questionnaires have been implemented
    } else {
      this.updateHasQuestionnaire(this.enableQuestionnaire);
    }
  }

  // A free course indicates that course packages should be removed.
  @Watch('freeCourse')
  onFreeCourseToggle() {
    if (
      !this.isInitializing &&
      this.activeCoursePackages.length > 0 &&
      !this.hasDefaultCoursePackage &&
      this.coursePackagesHaveValues &&
      this.freeCourse !== this.isFreeCourse &&
      this.hasPricedCoursePackages
    ) {
      this.openDisableCoursePackageModal();
    } else {
      this.updateIsFreeCourse(this.freeCourse);
    }
  }

  @Watch('hasModules', { immediate: true })
  onModuleStateValueChanged() {
    if (this.isSettings && this.enableModules !== this.hasModules) {
      this.enableModules = this.hasModules;
    }
  }

  @Watch('hasQuestionnaires', { immediate: true })
  onQuestionnaireStateValueChanged() {
    if (
      this.isSettings &&
      this.enableQuestionnaire !== this.hasQuestionnaires
    ) {
      this.enableQuestionnaire = this.hasQuestionnaires;
    }
  }

  @Watch('isFreeCourse', { immediate: true })
  onIsFreeStateValueChanged() {
    if (this.isSettings && this.freeCourse !== this.isFreeCourse) {
      this.freeCourse = this.isFreeCourse;
    }
  }
}
